import { useCallback, useContext, useMemo } from 'react';
import styled from '@emotion/styled';
import { ChatActionType, ChatHintType } from '@trinity/chat';
import type { Chat, Chart } from '@trinity/chat';
import { ChartLegend } from '@trinity/chat-components/components/Chat.Charts/Legend.js';
import { ChartInfoContext, ChartTypeContext, ChartGroupByContext, SubmitActionContext, ChartViewContext } from './context';
import { BasicMarkdown } from '../Markdown/Markdown';
import { MessageQueryHint, MessageQueryHintContainer } from '../presentation/Message.QueryHint';
import { BacktrackButton } from '../Chat.Charts/BackTrack';
import { BrandInsightsModalContext } from '../BrandInsights/context';
import { ChartViewContainer } from './Chat.Chat.ViewContainer';
import { useNavigate } from '@trinity/chat-components/containers/Chat/RoutingProvider';
import { detectUnitFromRecords } from '@trinity/chat-components/utils/chart';
import * as $navigation from '@trinity/chat-components/utils/navigation'
import { useRenderResponseChart, useResponseChartLegend } from './Message.Response.Chart.hooks';

export const ChartQuantAnalysesResponse = () => {
  const navigate = useNavigate();
  const [isOpen, toggleModal] = useContext(BrandInsightsModalContext);
  const [chart] = useContext(ChartInfoContext);
  const [chartType] = useContext(ChartTypeContext);
  const ctx = useContext(SubmitActionContext);
  const [viewMode] = useContext(ChartViewContext);

  const unitInfo = useMemo(() => {
    if (!chart?.table?.records) return null;
    return detectUnitFromRecords(chart.table.records);
  }, [chart?.table?.records]);

  const legend = useResponseChartLegend();
  const renderChart = useRenderResponseChart({ legend, unitInfo });

  const handleClick = () => {
    const location = $navigation.project.quantAnalysis({
      slug: chart.projectId.toString(),
      quantAnalysisId: chart.quantId,
      itemId: chart.quantItemId,
    }).pathname;

    navigate(location);
    toggleModal();
  };

  const handleBacktrack = useCallback(() => {
    return ctx({
      type: ChatActionType.FindQuantAnalyses,
      quants: chart.quants,
      query: chart.query,
      projectId: chart.projectId,
    }, chart.query);
  }, [ctx, chart.projectId, chart.query, chart.quants]);

  let message = '';
  const response = chart?.chart?.response ?? 'No response available. Please try again.';

  if (!chart.footer || chart.footer.length === 0) {
    message += response;
  }
  else {
    const split = chart.footer[0].split('  ');
    // format it e.g. [A, B] => A and B [A, B, C] => A, B and C
    const last = split.pop();
    const lastFormatted = last ? ` and ${last}` : '';
    const rest = split.length > 0 ? `${split.join(',')}${lastFormatted}` : '';
    const metrics = rest ? ` The metrics are ${rest}.` : ` The metric is ${last}.`;
    message = response + metrics;
  }

  if (!chart.chart || !chart.chart.chart || chart.chart.type === 'none') {
    const hint: Chat.QueryHint.Item = {
      displayValue: chart.title,
      type: ChatHintType.Text,
    };

    // the response should only be displayed if chart.chart is not null
    return (
      <div>
        {chart.chart &&
          <BasicMarkdown>{message}</BasicMarkdown>
        }

        <NoChartMessage>
          <div>Sorry, I was not able to generate a chart for this analysis. Go to analysis tab by clicking below:</div>
          <br />
          <MessageQueryHintContainer>
            <MessageQueryHint
              key={chart.projectId}
              hint={hint}
              onClick={() => handleClick()} />
          </MessageQueryHintContainer>

          <br />
          <TableWrapper>
            {/* <AnalysisTable data={chart.table} /> */}
          </TableWrapper>
          <br />
        </NoChartMessage>

        <BacktrackButton quants={chart.quants} onClick={handleBacktrack} />
      </div>
    );
  }

  return (
    <div>
      <BasicMarkdown>{message}</BasicMarkdown>
      {chart.showWarning && (
        <WarningText>{`Sample size is low (N<20). Please interpret with caution.`}</WarningText>
      )}

      <Chart>
        <ChartViewContainer
          renderChart={() => renderChart(chartType)}
          chartType={chartType} />
        {chart.chart.chart && (
          <>
            {chart.footer && chart.footer.length > 0 && (
              <Footer>{chart.footer[0]}</Footer>
            )}
            {legend.length > 0 && viewMode === 'chart' && <ChartLegend legend={legend} />}
          </>
        )}
      </Chart>
      <BacktrackButton quants={chart.quants} onClick={handleBacktrack} />
    </div>

  );
};

const Chart = styled.div`
  border: 1px solid var(--gray-l);
  border-radius: 15px;
  padding: 12px;
  margin-top: 10px;
  background-color: white;
`;

const NoChartMessage = styled.div`
  margin-top: 10px;
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  max-width: 100%;
`;

const Footer = styled.div`
  font-family: var(--font-semibold);
  font-size: 14px;
  margin-bottom: 10px;
`;

const WarningText = styled.div(({ theme }) => ({
  fontFamily: theme.fonts.semiBold,
  color: 'red',
  marginTop: '10px',
  marginBottom: '10px',
  fontSize: '14px',
}));